import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { IArenaDto } from '@models/arena/dtos/arena-dto';
import { IArenaForShow } from '@models/arena/dtos/arena-for-show';
import { ICategoryDto } from '@models/category-dto';
import { ICompetitionBlockDto } from '@models/competition-block-dto';
import { ICompetitionDto } from '@models/competition-dto';
import { ITopSeedDto } from '@models/draw/dtos/top-seed-dto';
import { DrawPostForm } from '@models/draw/forms/draw-post-form';
import { MetadataPutEnum } from '@models/enums/metadata-put-enum';
import { IEventDto } from '@models/event-dto';
import { ListAthleteEnrollmentsDto } from '@models/list-athlete-enrollments-dto';
import { IPage } from '@models/pagination/page';
import { PageRequest } from '@models/pagination/page-request';
import { InputTypeEnum } from '@shared/components/input-container/input-container.component';
import { ArenaService } from '@shared/services/arena.service';
import { ChampionshipService } from '@shared/services/championship.service';
import { CompetitionBlockService } from '@shared/services/competition-block.service';
import { DrawService } from '@shared/services/draw.service';
import { EnrollmentAthleteService } from '@shared/services/enrollment-athlete.service';
import { EventService } from '@shared/services/event.service';
import { Subject } from 'rxjs';
import { BaseComponent } from 'src/app/core/utilities/base-component/base-component';

@Component({
  selector: 'sigecom-draw-create',
  templateUrl: './draw-create.component.html',
})
export class DrawCreateComponent extends BaseComponent implements OnInit {
  drawPostForm: DrawPostForm = new DrawPostForm();

  InputTypeEnum = InputTypeEnum;

  sorNrId?: number;
  comNrId?: number;
  bloNrId?: number;
  eveNrId?: number;

  protected events?: IPage<IEventDto>;
  protected competitionsBlocks?: IPage<ICompetitionBlockDto>;
  protected competitions?: IPage<ICompetitionDto>;
  protected categories?: IPage<ICategoryDto>;
  protected pagedArenas?: IPage<IArenaDto>;
  protected categoryInfo?: ICategoryDto;
  protected enrollmentsByCategory?: ListAthleteEnrollmentsDto[];
  protected keyHeaders: Array<ITopSeedDto> = [];
  protected registeredsCategory?: IPage<ListAthleteEnrollmentsDto>;
  protected arenaValue?: number;
  protected selectedsArenas: Array<IArenaForShow> = [];

  private competitionBlockSubject: Subject<number> = new Subject();
  private competitionSubject: Subject<number> = new Subject();

  isEditing: boolean = false;

  findCategoryByProva?: string;

  #championshipService = inject(ChampionshipService);
  #eventService = inject(EventService);
  #drawService = inject(DrawService);
  #competitionBlockService = inject(CompetitionBlockService);
  #arenaService = inject(ArenaService);
  #cdr = inject(ChangeDetectorRef);
  #enrollmentAthleteService = inject(EnrollmentAthleteService);

  ngOnInit(): void {
    this.activatedRoute.paramMap.subscribe((params) => {
      const sorNrId = params.get('sorNrId');
      if (sorNrId) {
        this.sorNrId = +sorNrId;
        this.isEditing = true;
        this.findDrawById(this.sorNrId);
      }
    });

    this.loadEvents();
    this.loadCompetitionsByBlock();
    this.#arenaService
      .findArenas$({}, PageRequest.default())
      .subscribe((arenas) => {
        this.pagedArenas = arenas;
      });
  }

  protected drawList() {
    this.router.navigate(['sorteios']).then();
  }

  protected onSubmit(form: NgForm) {
    this.drawPostForm.entTxMetadado = this.isEditing
      ? MetadataPutEnum.ALTERADO
      : MetadataPutEnum.CRIADO;

    if (this.isEditing && this.sorNrId) {
      this.drawPostForm.entNrId = this.sorNrId;
    }

    const request$ =
      this.isEditing && this.sorNrId
        ? this.#drawService.editDraw$(this.sorNrId, this.drawPostForm)
        : this.#drawService.saveDraw$(this.drawPostForm);

    request$.subscribe({
      next: () => {
        const msg = this.isEditing
          ? 'Sorteio editado com sucesso!'
          : 'Sorteio cadastrado com sucesso!';
        this.snackbarService.showSuccess(msg);
        this.drawPostForm = new DrawPostForm();
        form.resetForm();
        this.router.navigate(['sorteios']);
      },
    });
  }

  onParticipantChange(enrollment: ListAthleteEnrollmentsDto) {
    if (
      this.keyHeaders.findIndex(
        (value) => value.incTxResumo === enrollment.insTxResumo
      ) >= 0
    ) {
      return;
    }

    this.keyHeaders.push({
      incNrId: enrollment.insNrId,
      incTxResumo: enrollment.insTxResumo,
    });

    this.drawPostForm.incNrIds?.push(enrollment.insNrId!);
  }

  getParticipantName(atlNrId: number): string {
    return this.registeredsCategory?.content?.find(
      (attachment) => attachment.insNrId === atlNrId
    )?.insTxResumo!;
  }

  removeParticipant(incNrId: number) {
    this.keyHeaders = this.keyHeaders.filter(
      (value) => value.incNrId !== incNrId
    );

    this.drawPostForm.incNrIds = this.drawPostForm.incNrIds?.filter(
      (id) => id !== incNrId
    );
  }

  loadCompetitionsBlocks($event: Event) {
    const selectedEventId = Number(($event.target as HTMLSelectElement).value);
    if (!selectedEventId) {
      this.competitionsBlocks = undefined;
      this.bloNrId = undefined;
      return;
    }
    this.eveNrId = selectedEventId;
    this.bloNrId = undefined;
    this.comNrId = undefined;
    this.categories = undefined;
    this.#eventService
      .getAllCompetitionBlocksByEvent$(selectedEventId)
      .pipe(this.takeUntilDestroy())
      .subscribe((result) => {
        this.competitionsBlocks = result;
      });
  }

  loadCategories($event: Event) {
    if (!this.comNrId) return;

    this.competitionSubject.next(this.comNrId);
    this.applyFiltersToCategories();
  }

  applyFiltersToCategories(callback?: () => void) {
    if (!this.comNrId) return;

    this.#championshipService
      .getAllCategoryByCompetition(this.comNrId, 0, 100)
      .pipe(this.takeUntilDestroy())
      .subscribe({
        next: (result) => {
          this.categories = result;

          if (callback) {
            callback();
          }
        },
        error: (err) => {
          this.messageErrorResolver.resolveMessage(err);
        },
      });
  }
  changeCategory() {
    const selectedCategoryId = this.drawPostForm.catNrId;

    if (!selectedCategoryId) {
      this.registeredsCategory = undefined;
      return;
    }

    this.categoryInfo = this.categories?.content?.find(
      (category) => category.catNrId === selectedCategoryId
    );

    if (!this.categoryInfo) return;

    this.loadEnrollmentsByCategory();
  }

  loadCompetitions($event: Event) {
    const selectedBlockId = Number(($event.target as HTMLSelectElement).value);
    if (!selectedBlockId) {
      this.competitions = undefined;
      this.comNrId = undefined;
      return;
    }
    this.bloNrId = selectedBlockId;
    this.comNrId = undefined;
    this.categories = undefined;

    this.competitionBlockSubject.next(selectedBlockId);
  }

  loadEvents(): void {
    this.#eventService
      .getAllAvailableEvents(0, 100)
      .pipe(this.takeUntilDestroy())
      .subscribe({
        next: (result) => {
          this.events = result;
        },
        error: (err) => {
          this.messageErrorResolver.resolveMessage(err);
        },
      });
  }

  private findDrawById(sorNrId: number): void {
    this.#drawService.findDrawById$(sorNrId).subscribe({
      next: (draw) => {
        this.drawPostForm.sorTxNome = draw.sorTxNome || '';
        this.drawPostForm.sorTxDescricao = draw.sorTxDescricao || '';
        this.drawPostForm.sorNrMaxJogosDia = draw.sorNrMaxJogosDia ?? undefined;
        this.drawPostForm.sorNrIntervaloJogos =
          draw.sorNrIntervaloJogos ?? undefined;
        this.drawPostForm.sorNrMaxEmbSimultaneos =
          draw.sorNrMaxEmbSimultaneos ?? undefined;
        this.drawPostForm.sorBlTrocaOrdemConfronto =
          draw.sorBlTrocaOrdemConfronto ?? false;

        this.comNrId = draw.sorObjCategoria?.comNrId;
        const categoriaId = draw.sorObjCategoria?.catNrId;

        this.#championshipService.getCompetitionById(this.comNrId!).subscribe({
          next: (competition) => {
            this.eveNrId = competition.eveNrId;
            this.bloNrId = competition.bloNrId;

            this.applyFiltersToCategories(() => {
              this.drawPostForm.catNrId = categoriaId;
              this.loadEnrollmentsByCategory();

              if (draw.sorListCabecasChave) {
                const validIds = draw.sorListCabecasChave
                  .map((c) => c.incNrId)
                  .filter((id): id is number => id !== undefined);

                this.keyHeaders = draw.sorListCabecasChave.filter((c) =>
                  validIds.includes(c.incNrId!)
                );

                this.drawPostForm.incNrIds = [...validIds];
              }
            });

            this.#cdr.detectChanges();
          },
        });

        if (
          Array.isArray(draw.sorListArenas) &&
          draw.sorListArenas.length > 0
        ) {
          const arenaFromApi = draw.sorListArenas[0];

          this.arenaValue = arenaFromApi.areNrId;

          if (!this.drawPostForm.arenaSorteioFormList) {
            this.drawPostForm.arenaSorteioFormList = [];
          }

          if (this.drawPostForm.arenaSorteioFormList.length === 0) {
            this.drawPostForm.arenaSorteioFormList.push({
              areNrId: arenaFromApi.areNrId,
              arsNrEmbatesSimultaneos:
                arenaFromApi.arsNrMaximoEmbateSimultaneos ?? undefined,
            });
          } else {
            this.drawPostForm.arenaSorteioFormList[0].areNrId =
              arenaFromApi.areNrId;
          }
        }

        this.#cdr.detectChanges();
      },
      error: (err) => {
        this.messageErrorResolver.resolveMessage(err);
      },
    });
  }

  private loadCompetitionsByBlock(): void {
    this.competitionBlockSubject
      .pipe(this.takeUntilDestroy())
      .subscribe((blockId) => {
        this.#competitionBlockService
          .getAllCompetitionByBlocks(blockId)
          .subscribe({
            next: (result) => {
              this.competitions = result;
            },
            error: (err) => {
              this.messageErrorResolver.resolveMessage(err);
            },
          });
      });
  }

  removeArena(areNrId: number) {
    if (!this.selectedsArenas) return;

    this.selectedsArenas = this.selectedsArenas.filter(
      (value) => value.areNrId !== areNrId
    );
  }

  setArena(arena: HTMLSelectElement) {
    const arenaId = Number(arena.value);

    if (!this.drawPostForm.arenaSorteioFormList) {
      this.drawPostForm.arenaSorteioFormList = [];
    }

    if (this.drawPostForm.arenaSorteioFormList.length === 0) {
      this.drawPostForm.arenaSorteioFormList.push({
        areNrId: arenaId,
        arsNrEmbatesSimultaneos: undefined,
      });
    } else {
      this.drawPostForm.arenaSorteioFormList[0].areNrId = arenaId;
    }

    this.arenaValue = arenaId;

    if (!isNaN(arenaId)) {
      this.#arenaService.findArenasById$(arenaId).subscribe((value) => {
        this.selectedsArenas?.push({
          areNrId: value.areNrId,
          areTxNome: value.areTxNome,
        });
        this.#cdr.detectChanges();
      });
    }

    this.#cdr.detectChanges();
  }

  loadEnrollmentsByCategory() {
    if (
      !this.eveNrId ||
      !this.bloNrId ||
      !this.comNrId ||
      !this.drawPostForm.catNrId
    )
      return;

    const filter = {
      eveNrId: this.eveNrId,
      bloNrId: this.bloNrId,
      comNrId: this.comNrId,
      catNrId: this.drawPostForm.catNrId,
    };

    this.#enrollmentAthleteService
      .findAllAthleteEnrollments(filter, 0, 100)
      .pipe(this.takeUntilDestroy())
      .subscribe({
        next: (res) => {
          this.registeredsCategory = res;
          this.#cdr.detectChanges();
        },
        error: (err) => {
          this.messageErrorResolver.resolveMessage(err);
        },
      });
  }

  setEmbateMaxInForm(arsNrEmbatesSimultaneos: HTMLInputElement) {
    let idx = this.selectedsArenas?.findIndex(
      (value) => value.arsNrEmbatesSimultaneos! === undefined
    );

    let idxForm = this.drawPostForm.arenaSorteioFormList?.findIndex(
      (value) => value.arsNrEmbatesSimultaneos! === undefined
    );

    this.selectedsArenas![idx!].arsNrEmbatesSimultaneos = Number(
      arsNrEmbatesSimultaneos.value
    );

    this.drawPostForm.arenaSorteioFormList![idxForm!].arsNrEmbatesSimultaneos =
      Number(arsNrEmbatesSimultaneos.value);
  }
}
